/**
 * The global date range lives in the URL so links and reloads keep it: ?days=30 for the last 30
 * complete days, or ?from=2026-09-01&to=2026-09-30 for dates picked by hand (account-local days).
 */
import { daysBetween } from "@brecx/shared";
import { useSearchParams } from "react-router";

export const RANGES = [7, 14, 30, 60, 90] as const;
/** The longest range the API reads. */
export const MAX_RANGE_DAYS = 400;

const DATE = /^\d{4}-\d{2}-\d{2}$/;

export interface Range {
  /** how many days the range covers */
  days: number;
  /** the range as the API's query string */
  query: string;
  /** the dates picked by hand; null while one of the "last N days" ranges is on */
  custom: { from: string; to: string } | null;
  setDays: (d: number) => void;
  setCustom: (from: string, to: string) => void;
}

export function useRange(): Range {
  const [params, setParams] = useSearchParams();
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  const span = DATE.test(from) && DATE.test(to) && from <= to ? daysBetween(from, to) + 1 : 0;
  const custom = span > 0 && span <= MAX_RANGE_DAYS ? { from, to } : null;
  const raw = Number(params.get("days") ?? 30);
  const days = (RANGES as readonly number[]).includes(raw) ? raw : 30;
  return {
    days: custom ? span : days,
    query: custom ? `from=${custom.from}&to=${custom.to}` : `days=${days}`,
    custom,
    setDays: (d) =>
      setParams((p) => {
        p.set("days", String(d));
        p.delete("from");
        p.delete("to");
        return p;
      }),
    setCustom: (a, b) =>
      setParams((p) => {
        p.set("from", a);
        p.set("to", b);
        return p;
      }),
  };
}
